import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'hooks';

import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';

// routing
import Routes from './routes';

// defaultTheme
import themes from './themes';

// project imports
import NavigationScroll from './layout/NavigationScroll';

import { setIsConnected, ready } from "store/applicationReducer";
import { getUserInfo } from "store/userReducer";
import { getCustomization } from "store/customizationReducer";
import { LOGOUT_ACTION } from 'store/rootReducer';

import webSocketManager, { ConnectionStatus } from 'socket/WebSocketManager';

import ErrorBoundary from 'utils/ErrorBoundary';
import { setupInterceptors } from "utils/setupInterceptors";
setupInterceptors();

import LoadingSpinner from 'ui-components/LoadingSpinner';

import NotFound from "./views/NotFound";

// ==============================|| APP ||============================== //

const App = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const application = useAppSelector((state) => state.application);
    const { isConnected, isReady } = application;
    const user = useAppSelector((state) => state.user);
    const { info: userInfo } = user;
    const customization = useAppSelector((state) => state.customization);
    const { isLoaded } = customization;
    const requestLocation = useRef<string | null>(null);

    const sessionTimer = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        console.debug(`ready: ${isReady}, connected: ${isConnected}`)
        const location = window.location.pathname + window.location.search + window.location.hash;
        console.debug(`location : ${location}`);
        requestLocation.current = location;
        if (!userInfo) onCheckUser();

        webSocketManager.connect()
            ?.then(() => {
                dispatch(setIsConnected(true));
            })
            .catch((e) => {
                console.error(e);
                dispatch(setIsConnected(false));
            }).finally(() => {
                dispatch(ready());
            });

        const handleStatus = (status: ConnectionStatus) => {
            if (status === 'disconnected') dispatch(setIsConnected(false));
            if (!isConnected && status === 'connected') dispatch(setIsConnected(true));
        };

        webSocketManager.addStatusListener(handleStatus);
        window.addEventListener('focus', onCheckUser);

        return () => {
            webSocketManager.disconnect();
            window.removeEventListener('focus', onCheckUser);
        }
    }, []);

    useEffect(() => {
        if (isConnected) {
            webSocketManager.send('/session/check', { hi: 'hi' });

            if (!sessionTimer.current) {
                webSocketManager.subscribe('/user/session', (message) => {
                    console.log('[RECV] /user/session:', message.body);
                    if (!userInfo) onCheckUser();
                });

                webSocketManager.subscribe('/user/errors', (message) => {
                    console.warn('[RECV] /user/errors:', message.body);
                    if (message.body === 'Unauthorized' && !!userInfo) {
                        dispatch({type: LOGOUT_ACTION});
                    }
                });

                sessionTimer.current = setInterval(() => {
                    webSocketManager.send('/session/check', { hi: 'hi' });
                }, 1000 * 30);
            }
        } else {
            if (sessionTimer.current) {
                clearInterval(sessionTimer.current);
                sessionTimer.current = null;
            }

            webSocketManager.unsubscribe('/user/session');
            webSocketManager.unsubscribe('/user/errors');
        }

        return () => {
            if (sessionTimer.current) {
                clearInterval(sessionTimer.current);
                sessionTimer.current = null;
            }

            webSocketManager.unsubscribe('/user/session');
            webSocketManager.unsubscribe('/user/errors');
        };
    }, [isConnected]);

    useEffect(() => {
        if (userInfo) {
            dispatch(getCustomization());
            if (requestLocation.current) {
                navigate(requestLocation.current);
                requestLocation.current = null;
            }
        } else {
            navigate('/login');
        }
    }, [userInfo]);

    const onCheckUser = () => {
        dispatch(getUserInfo());
    }

    const render = () => {
        console.debug("Render App.");
        if (!isReady) return;
        if (!isConnected) return (<NotFound/>);

        if (!!userInfo && !isLoaded) return (<LoadingSpinner/>)

        return (
            <ErrorBoundary>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <StyledEngineProvider injectFirst>
                        <ThemeProvider theme={themes(customization)}>
                            <CssBaseline/>
                            <NavigationScroll>
                                <Routes/>
                            </NavigationScroll>
                        </ThemeProvider>
                    </StyledEngineProvider>
                </LocalizationProvider>
            </ErrorBoundary>
        )
    }

    return render()
};

export default App;
